/**
 * SOLID Review
 * S — Strong. One concern: an adapter's `Result` (failure as an `Error`) as a port's (failure as data).
 * O — Strong. Any adapter result; nothing here names one.
 * L — N/A.
 * I — Strong. Two functions.
 * D — Strong. The shared Result type and the Application's failure code.
 */
import type { Result } from '../../shared/Result';
import { SERVER_REFUSED } from '../../application/ports/contracts';

type TPortResult<T> = { readonly success: true; readonly value: T } | { readonly success: false; readonly error: { readonly code: string; readonly message: string } };

/**
 * An Apollo adapter's answer at a contracted port (frontend strict-ports plan, FE1, 2026-10-03). The
 * adapters fail with `Error` objects, which do not survive `JSON.stringify`. A port fails with
 * `{ code, message }`. The message is kept verbatim, because the lobby shows it ("…returned a GraphQL
 * error: Not enough players"). The code is `SERVER_REFUSED`: the adapters keep the server's code only inside
 * their message.
 */
export function fromAdapter<T, U = T>(result: Result<T>, map?: (value: T) => U): TPortResult<U> {
  if (!result.success) return { success: false, error: { code: SERVER_REFUSED, message: result.error.message } };
  return { success: true, value: map === undefined ? (result.value as unknown as U) : map(result.value) };
}

/** An adapter's answer where the port promises nothing back. */
export function doneFromAdapter(result: Result<unknown>): TPortResult<void> {
  return fromAdapter(result, () => undefined);
}
